/**
 * Warmer / colder.
 *
 * When a guess was one of three, telling the seeker "1,284 km" was generous:
 * they had no shots left to spend on the information. Now that guesses are
 * cheap and plentiful, an exact distance is not a hint, it is a solution —
 * three of them determine the hidden point by trilateration, and the round
 * becomes arithmetic on guess four. Bands keep the feedback loop tight enough
 * to hunt on and coarse enough to still be a hunt.
 *
 * The stops are multiples of the arena's own win radius, so the ramp means the
 * same thing on a country as it does on a planet. At the planetary radius of
 * 25 km they land on 25 / 50 / 100 / 250 / 750 / 2,000 km, which is the ramp
 * the brand kit was already calibrated to.
 */

/** Coldest last, so the index rises with distance. */
export const WARMTH_LABELS = [
  "Found it",
  "Boiling",
  "Burning",
  "Hot",
  "Warm",
  "Cool",
  "Ice cold",
] as const;

export type WarmthLabel = (typeof WARMTH_LABELS)[number];

/** Upper edge of each band, as a multiple of the win radius. */
const EDGES = [1, 2, 4, 10, 30, 80, Infinity];

export const COLDEST_BAND = WARMTH_LABELS.length - 1;

/** Which band a distance falls in. 0 is a win, 6 is the wrong hemisphere. */
export function warmthBand(distanceKm: number, winRadiusKm: number): number {
  const ratio = distanceKm / Math.max(1e-6, winRadiusKm);
  return EDGES.findIndex((edge) => ratio <= edge);
}

export function warmthLabel(band: number): WarmthLabel {
  return WARMTH_LABELS[Math.min(COLDEST_BAND, Math.max(0, band))];
}

export type Trend = "warmer" | "colder" | "same" | null;

/** Compared with the guess before it. Null for the first guess of a round. */
export function trendBetween(
  previousKm: number | null,
  currentKm: number,
): Trend {
  if (previousKm === null) return null;
  // A metre of improvement is not news. Anything under a percent reads as a
  // sideways step, which is what it is.
  const delta = (previousKm - currentKm) / Math.max(1, previousKm);
  if (delta > 0.01) return "warmer";
  if (delta < -0.01) return "colder";
  return "same";
}
